// Own profile — the signed-in user's own account (GET users/own-profile), the
// source for the /my-profile page. The response is user-scoped identity plus a
// `companies[]` array of their membership in each company; the mapper keeps
// `companies[0]` as `membership` and nests that company's settings fields under
// `membership.company`.
//
// Confirmed against live samples (2026-09-29), element shapes included. Note
// `userGroups` names its label `groupName`, not `name` — it is the one list here
// that does, so the mapper renames it on the way out.

import { baseApi } from '../baseApi';
import { ROUTES } from '../routes';
import type { AppLanguage } from '@/redux/language/languageSlice';
import { otpRequested } from '@/redux/otp/otpSlice';
import { emailOtpKey } from '@/redux/otp/otpKeys';

interface ApiOwnProfileCompany {
  _id: string;
  userGroups: { _id: string; groupName: string }[];
  roles: { _id: string; name: string; isOwner: boolean }[];
  // The level rides along on each node (same arrangement as the People list), so
  // placement chips read its icon/name directly — no levels lookup needed.
  nodes: { _id: string; name: string; level: { _id: string; icon: string; name: string } }[];
  projects: { _id: string; title: string; icon: string }[];
  companyName: string;
  nameOnMidani: string | null;
  brandColorScheme: string | null;
  welcomeScreen: string | null;
}

interface ApiOwnProfile {
  _id: string;
  appLanguage: string;
  countryCode: string;
  email: string | null;
  emailLoginEnabled: boolean;
  emailVerified: boolean;
  firstName: string;
  lastName: string;
  mobileNumber: string;
  lastPasswordUpdateDate: string | null;
  companies: ApiOwnProfileCompany[];
}

/** The company's own settings, grouped out of the flat membership payload. */
export interface OwnProfileCompany {
  name: string;
  nameOnMidani: string | null;
  brandColorScheme: string | null;
  welcomeScreen: string | null;
}

/** What the user is inside the active company — the Membership card's data.
 *  `nodes` matches PersonDetail.units and `projects` matches
 *  PersonListItem.projects on purpose, so the chips reuse the People markup. */
export interface OwnProfileMembership {
  /** companies[0]._id — the user's company-user record id. */
  id: string;
  groups: { id: string; name: string }[];
  roles: { id: string; name: string; isOwner: boolean }[];
  nodes: { id: string; name: string; levelName: string; levelIcon: string }[];
  projects: { id: string; title: string; icon: string }[];
  company: OwnProfileCompany;
}

export interface OwnProfile {
  id: string;
  firstName: string;
  lastName: string;
  fullName: string;
  /** '' when the account has no email yet (the payload sends null). */
  email: string;
  emailVerified: boolean;
  // Raw parts kept beside the composed `phone` (same as PersonDetail) so an
  // editor can use them if phone ever becomes editable here.
  countryCode: string;
  mobileNumber: string;
  phone: string;
  appLanguage: AppLanguage;
  /** ISO timestamp; '' when the password has never been changed. */
  lastPasswordUpdateDate: string;
  membership: OwnProfileMembership;
}

const toOwnProfile = (d: ApiOwnProfile): OwnProfile => {
  // Bare index, deliberately: the route is authToken-scoped, so the response
  // carries exactly the active company. Don't add a fallback here.
  const c = d.companies[0];

  return {
    id: d._id,
    firstName: d.firstName,
    lastName: d.lastName,
    fullName: `${d.firstName} ${d.lastName}`.trim(),
    email: d.email ?? '',
    emailVerified: d.emailVerified,
    countryCode: d.countryCode,
    mobileNumber: d.mobileNumber,
    phone: d.mobileNumber ? `${d.countryCode} ${d.mobileNumber}`.trim() : '',
    appLanguage: d.appLanguage === 'ar' ? 'ar' : 'en',
    lastPasswordUpdateDate: d.lastPasswordUpdateDate ?? '',
    membership: {
      id: c._id,
      groups: c.userGroups.map((g) => ({ id: g._id, name: g.groupName })),
      roles: c.roles.map((r) => ({ id: r._id, name: r.name, isOwner: r.isOwner })),
      nodes: c.nodes.map((n) => ({
        id: n._id,
        name: n.name,
        levelName: n.level.name,
        levelIcon: n.level.icon,
      })),
      projects: c.projects.map((p) => ({ id: p._id, title: p.title, icon: p.icon })),
      company: {
        name: c.companyName,
        nameOnMidani: c.nameOnMidani,
        brandColorScheme: c.brandColorScheme,
        welcomeScreen: c.welcomeScreen,
      },
    },
  };
};

/** POST users/update-own-profile body. `email` always rides along, as `null`
 *  when cleared — which is also how the GET returns an account without one. */
export interface UpdateOwnProfileInput {
  firstName: string;
  lastName: string;
  email: string | null;
  appLanguage: AppLanguage;
}

/** POST users/update-password body. Plaintext; the axios client encrypts the
 *  whole payload transparently, as it does for login/register. */
export interface UpdatePasswordInput {
  oldPassword: string;
  newPassword: string;
}

export const ownProfileApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getOwnProfile: build.query<OwnProfile, void>({
      query: () => ({ ...ROUTES.users.ownProfile }),
      transformResponse: (res: ApiOwnProfile) => toOwnProfile(res),
      providesTags: ['Profile'],
    }),
    updateOwnProfile: build.mutation<void, UpdateOwnProfileInput>({
      query: (body) => ({ ...ROUTES.users.updateOwnProfile, data: body }),
      // Refetching is what re-seeds the page's draft — no manual bookkeeping.
      invalidatesTags: ['Profile'],
    }),
    // No tags: sending a code changes nothing the page shows. `emailVerified`
    // only flips once the code is entered, so invalidating would refetch for
    // nothing.
    // The arg is NOT sent — the request has no body; the server mails the SAVED
    // address. It only keys the resend cooldown, and it is the same value
    // VerifyEmailModal displays and reads, so the two can't disagree.
    sendEmailVerificationOtp: build.mutation<void, string>({
      query: () => ({ ...ROUTES.users.sendEmailVerificationOtp }),
      onQueryStarted: async (email, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled;
          dispatch(otpRequested({ key: emailOtpKey(email), at: Date.now() }));
        } catch {
          /* no cooldown on a failed send */
        }
      },
    }),
    // This IS the call that flips `emailVerified`, so unlike the send above it
    // invalidates. Note the reach: RTK Query's cache is per JS runtime, so this
    // only refreshes a /my-profile mounted in the SAME tab. The usual case —
    // link opened from a mail client in a new tab — is not covered by cache
    // invalidation at all; that tab simply fetches fresh on its own.
    verifyEmail: build.mutation<void, { code: string }>({
      query: (body) => ({ ...ROUTES.users.verifyEmail, data: body }),
      invalidatesTags: ['Profile'],
    }),
    // Invalidates because `lastPasswordUpdateDate` lives on the profile and
    // feeds the Password card's "Last changed" line — without the refetch it
    // would sit stale until a reload.
    updatePassword: build.mutation<void, UpdatePasswordInput>({
      query: (body) => ({ ...ROUTES.users.updatePassword, data: body }),
      invalidatesTags: ['Profile'],
    }),
  }),
});

export const {
  useGetOwnProfileQuery,
  useUpdateOwnProfileMutation,
  useSendEmailVerificationOtpMutation,
  useVerifyEmailMutation,
  useUpdatePasswordMutation,
} = ownProfileApi;
